import { doc, setDoc } from 'firebase/firestore';
import { db } from './config';

// PKCE helper fonksiyonları (Twitter için)
const generateRandomString = (length) => {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let result = '';
  const values = new Uint8Array(length);
  window.crypto.getRandomValues(values);
  for (let i = 0; i < length; i++) {
    result += charset[values[i] % charset.length];
  }
  return result;
};

const generateCodeChallenge = async (codeVerifier) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

// Instagram OAuth başlat (Facebook Login kullanarak)
// Instagram artık doğrudan OAuth kullanmıyor, Facebook üzerinden erişim sağlanıyor
export const connectInstagram = (userId) => {
  return new Promise((resolve, reject) => {
    const appId = process.env.REACT_APP_FACEBOOK_APP_ID;
    const redirectUri = `${window.location.origin}/oauth/callback`;
    // Instagram için gerekli scope'lar (posting için instagram_content_publish gerekli)
    const scope = 'pages_show_list,instagram_basic,pages_read_engagement,instagram_content_publish,business_management';

    const authUrl = `https://www.facebook.com/v21.0/dialog/oauth?client_id=${appId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${scope}&response_type=code&state=instagram_${userId}`;

    // Popup boyutları
    const width = 600;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    // Popup aç
    const popup = window.open(
      authUrl,
      'Instagram OAuth (Facebook Login)',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );

    if (!popup) {
      reject({ success: false, error: 'Popup engellendi. Lütfen popup engelleyicinizi kapatın.' });
      return;
    }

    // Popup'ın kapanmasını dinle
    const checkPopup = setInterval(() => {
      if (popup && popup.closed) {
        clearInterval(checkPopup);
        // Popup kapandı, başarılı varsay
        resolve({ success: true });
      }
    }, 500);
  });
};

// Facebook OAuth başlat (popup ile)
export const connectFacebook = (userId) => {
  return new Promise((resolve, reject) => {
    const appId = process.env.REACT_APP_FACEBOOK_APP_ID;
    const redirectUri = `${window.location.origin}/oauth/callback`;
    // Facebook için gerekli scope'lar (posting için)
    const scope = 'pages_show_list,pages_read_engagement,pages_manage_posts';

    const authUrl = `https://www.facebook.com/v21.0/dialog/oauth?client_id=${appId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${scope}&response_type=code&state=facebook_${userId}`;

    const width = 600;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    const popup = window.open(
      authUrl,
      'Facebook OAuth',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );

    if (!popup) {
      reject({ success: false, error: 'Popup engellendi. Lütfen popup engelleyicinizi kapatın.' });
      return;
    }

    const checkPopup = setInterval(() => {
      if (popup && popup.closed) {
        clearInterval(checkPopup);
        resolve({ success: true });
      }
    }, 500);
  });
};

// Twitter OAuth başlat (OAuth 2.0 PKCE - popup ile)
export const connectTwitter = async (userId) => {
  return new Promise(async (resolve, reject) => {
    const clientId = process.env.REACT_APP_TWITTER_CLIENT_ID;
    const redirectUri = `${window.location.origin}/oauth/callback`;
    const scope = 'tweet.read tweet.write users.read offline.access';

    // PKCE için code verifier ve challenge oluştur
    const codeVerifier = generateRandomString(128);
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    // Code verifier'ı localStorage'a kaydet (callback'te kullanılacak)
    localStorage.setItem('twitter_code_verifier', codeVerifier);

    const authUrl = `https://twitter.com/i/oauth2/authorize?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent(scope)}&state=twitter_${userId}&code_challenge=${codeChallenge}&code_challenge_method=S256`;

    const width = 600;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    const popup = window.open(
      authUrl,
      'Twitter OAuth',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );

    if (!popup) {
      reject({ success: false, error: 'Popup engellendi. Lütfen popup engelleyicinizi kapatın.' });
      return;
    }

    const checkPopup = setInterval(() => {
      if (popup && popup.closed) {
        clearInterval(checkPopup);
        resolve({ success: true });
      }
    }, 500);
  });
};

// LinkedIn OAuth başlat (popup ile)
export const connectLinkedIn = (userId) => {
  return new Promise((resolve, reject) => {
    const clientId = process.env.REACT_APP_LINKEDIN_CLIENT_ID;
    const redirectUri = `${window.location.origin}/oauth/callback`;
    const scope = 'openid profile w_member_social email';

    const authUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent(scope)}&state=linkedin_${userId}`;

    const width = 600;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    const popup = window.open(
      authUrl,
      'LinkedIn OAuth',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );

    if (!popup) {
      reject({ success: false, error: 'Popup engellendi. Lütfen popup engelleyicinizi kapatın.' });
      return;
    }

    const checkPopup = setInterval(() => {
      if (popup && popup.closed) {
        clearInterval(checkPopup);
        resolve({ success: true });
      }
    }, 500);
  });
};

// TikTok OAuth başlat
export const connectTikTok = (userId) => {
  return new Promise((resolve, reject) => {
    const clientKey = process.env.REACT_APP_TIKTOK_CLIENT_KEY;
    const redirectUri = `${window.location.origin}/oauth/callback`;
    const scope = 'user.info.basic,video.upload,video.publish';

    const authUrl = `https://www.tiktok.com/v2/auth/authorize?client_key=${clientKey}&scope=${encodeURIComponent(scope)}&response_type=code&redirect_uri=${encodeURIComponent(redirectUri)}&state=tiktok_${userId}`;

    const width = 600;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    const popup = window.open(
      authUrl,
      'TikTok OAuth',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );

    if (!popup) {
      reject({ success: false, error: 'Popup engellendi. Lütfen popup engelleyicinizi kapatın.' });
      return;
    }

    const checkPopup = setInterval(() => {
      if (popup && popup.closed) {
        clearInterval(checkPopup);
        resolve({ success: true });
      }
    }, 500);
  });
};

// YouTube OAuth başlat (Google OAuth kullanır)
export const connectYouTube = (userId) => {
  return new Promise((resolve, reject) => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = `${window.location.origin}/oauth/callback`;
    const scope = 'https://www.googleapis.com/auth/youtube.upload https://www.googleapis.com/auth/youtube';

    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&access_type=offline&state=youtube_${userId}`;

    const width = 600;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    const popup = window.open(
      authUrl,
      'YouTube OAuth',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );

    if (!popup) {
      reject({ success: false, error: 'Popup engellendi. Lütfen popup engelleyicinizi kapatın.' });
      return;
    }

    const checkPopup = setInterval(() => {
      if (popup && popup.closed) {
        clearInterval(checkPopup);
        resolve({ success: true });
      }
    }, 500);
  });
};

// Pinterest OAuth başlat
export const connectPinterest = (userId) => {
  return new Promise((resolve, reject) => {
    const clientId = process.env.REACT_APP_PINTEREST_CLIENT_ID;
    const redirectUri = `${window.location.origin}/oauth/callback`;
    const scope = 'boards:read,pins:read,pins:write';

    const authUrl = `https://www.pinterest.com/oauth/?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&state=pinterest_${userId}`;

    const width = 600;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    const popup = window.open(
      authUrl,
      'Pinterest OAuth',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );

    if (!popup) {
      reject({ success: false, error: 'Popup engellendi. Lütfen popup engelleyicinizi kapatın.' });
      return;
    }

    const checkPopup = setInterval(() => {
      if (popup && popup.closed) {
        clearInterval(checkPopup);
        resolve({ success: true });
      }
    }, 500);
  });
};

// Manuel paylaşım için Twitter intent
export const shareToTwitter = (text) => {
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
  window.open(twitterUrl, '_blank', 'width=600,height=400');
};

// Token'ı kaydet
export const saveOAuthToken = async (userId, platform, token, username, refreshToken = null, additionalData = {}) => {
  const data = {
    enabled: true,
    token: token,
    username: username,
    connectedAt: new Date().toISOString(),
    ...additionalData  // Backend'den gelen ekstra bilgiler (userId, pageId, pageName, name, vb.)
  };

  if (refreshToken) {
    data.refreshToken = refreshToken;
  }

  await setDoc(doc(db, 'users', userId, 'platforms', platform), data, { merge: true });
};